import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { To } from "react-router";
import { useLocation, useNavigate } from "react-router";
import type {
  NavigationGuardContextValue,
  Transition,
} from "./navigation-guard-context";
import { NavigationGuardContext } from "./navigation-guard-context";
import type { ViewTransitionDirection } from "./view-transition";
import { runViewTransition } from "./view-transition";

// Navigation-guard registry backing `useNavigationBlocker`
// (./use-navigation-blocker.ts). React Router's own `useBlocker` needs the
// data router (`createBrowserRouter`/`RouterProvider`), out of scope here.
//
// `attemptNavigate()` must be called by any navigation entry point that
// should respect an active guard. Currently wired: `<Button to="...">`
// only (`src/common/button/index.tsx`) — covers most in-app navigation UI
// via `<Link>`'s onClick-composition pattern.
//
// Known gaps:
//   - Raw `<Link>` usages elsewhere aren't wired to this registry.
//   - Imperative `navigate()` calls don't check it (mostly effect-driven
//     redirects, not user-initiated escapes, so lower risk).
//   - Browser back/forward isn't guarded — no way to intercept `popstate`
//     before the URL changes without the data router.
//   - Tab close/reload gets the browser's native confirmation, not the
//     app's popup (see `beforeunload` listener below).
//
// `pendingTransition` is shared context state, not per-guard — fine while
// `useNavigationBlocker` is the only consumer; revisit if a second
// concurrent consumer shows up.

export function NavigationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const guardsRef = useRef<Set<() => boolean>>(new Set());
  const [pendingTransition, setPendingTransition] = useState<Transition | null>(
    null
  );

  const registerGuard = useCallback((isActive: () => boolean) => {
    guardsRef.current.add(isActive);
    return () => {
      guardsRef.current.delete(isActive);
    };
  }, []);

  const isBlocked = useCallback(
    () =>
      Array.from(guardsRef.current).some((guardIsActive) => guardIsActive()),
    []
  );

  // React Router applies a navigation as a deferred React transition, so the
  // new screen is only in the DOM once the location commits (after any lazy
  // route has loaded). A view transition has to wait for exactly that.
  const location = useLocation();
  const resolveCommitRef = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    resolveCommitRef.current?.();
    resolveCommitRef.current = null;
  }, [location]);

  const performTransition = useCallback(
    ({ action, path, state, viewTransition }: Transition) => {
      const update = () => {
        void navigate(path, { replace: action === "REPLACE", state });
      };
      if (viewTransition) {
        runViewTransition(
          viewTransition,
          () =>
            new Promise<void>((resolve) => {
              resolveCommitRef.current = resolve;
              update();
              // Never leave the page frozen if the location doesn't change.
              setTimeout(resolve, 600);
            })
        );
      } else {
        update();
      }
    },
    [navigate]
  );

  const attemptNavigate = useCallback(
    (
      action: "PUSH" | "REPLACE",
      path: To,
      state?: unknown,
      viewTransition?: ViewTransitionDirection
    ) => {
      if (isBlocked()) {
        setPendingTransition({ action, path, state, viewTransition });
        return false;
      }
      performTransition({ action, path, state, viewTransition });
      return true;
    },
    [isBlocked, performTransition]
  );

  // Reads pendingTransition from closure rather than a functional updater:
  // calling performTransition() (navigate(), itself a setState) from
  // inside a state updater hits React's "update during render" trap.
  const confirmPendingTransition = useCallback(() => {
    if (pendingTransition) {
      performTransition(pendingTransition);
    }
    setPendingTransition(null);
  }, [pendingTransition, performTransition]);

  const cancelPendingTransition = useCallback(() => {
    setPendingTransition(null);
  }, []);

  // Tab close/reload: no custom UI possible, browser shows its own
  // confirmation while a guard is active.
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isBlocked()) {
        event.preventDefault();
        const unloadEvent = event;
        unloadEvent.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isBlocked]);

  const value = useMemo<NavigationGuardContextValue>(
    () => ({
      registerGuard,
      attemptNavigate,
      pendingTransition,
      confirmPendingTransition,
      cancelPendingTransition,
    }),
    [
      registerGuard,
      attemptNavigate,
      pendingTransition,
      confirmPendingTransition,
      cancelPendingTransition,
    ]
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
    </NavigationGuardContext.Provider>
  );
}
